/*
Create a reusable guard component that checks if a teacher is authenticated before allowing access to certain routes or components.
If the teacher is not authenticated, redirect them to the teacher login page.

*/
/**
 * RequireTeacherAuth
 * - Calls GET /api/session-context (credentials included)
 * - If verified -> renders children and passes sessionContext via render prop
 * - If not verified -> redirects to /teacher-login
 *
 * Usage:
 * <RequireTeacherAuth>
 *   {(session) => <TeacherPdfUploader session={session} />}
 * </RequireTeacherAuth>
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";


export default function RequireTeacherAuth({ children }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetch("/api/session-context", {
      method: "GET",
      credentials: "include", // REQUIRED for HttpOnly cookies
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`session-context failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;

        if (data && data.verified === true) {
          setSession(data);
          setLoading(false);
          return;
        }

        throw new Error("not verified");
      })
      .catch(() => {
        if (cancelled) return;
        navigate("/teacher-login", { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (loading) {
    return <div style={{ padding: 24 }}>Checking session…</div>;
  }

  // Render-prop style: children(session)
  return typeof children === "function" ? children(session) : children;
}
